import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Status } from '../../../domain/model/shipment/Shipment.types';
import type { RootState } from '../../../configuration/setup/store';
import { startOfToday, startOfTomorrow } from 'date-fns';
import { useAppSelector } from '../../../configuration/setup/hooks';

type DateRangeFilter = {
    from: string;
    to: string;
};

export const shipmentDateTypes = [
    'CALCULATED_DELIVERY_DATE',
    'PICKUP_CONFIRMATION_DATE',
    'REQUESTED_PICKUP_DATE',
] as const;

export type ShipmentDateType = (typeof shipmentDateTypes)[number];

const shipmentSortFields = [
    'pickupConfirmedAt',
    'calculatedDeliveryTimeAt',
    'requestedPickupAt',
    'status',
    'actions',
] as const;

export type ShipmentSortField = (typeof shipmentSortFields)[number];

export type ShipmentsOverviewState = {
    filter: {
        dateType: ShipmentDateType;
        dateRange: DateRangeFilter;
        status?: Status[];
        search: string;
        handlingUnitCodes: string[];
    };
    sorting: {
        key: ShipmentSortField;
        direction: 'asc' | 'desc';
    };
};

const initialState: ShipmentsOverviewState = {
    filter: {
        dateType: 'PICKUP_CONFIRMATION_DATE',
        dateRange: {
            from: startOfToday().toISOString(),
            to: startOfTomorrow().toISOString(),
        },
        status: undefined,
        search: '',
        handlingUnitCodes: [],
    },
    sorting: {
        key: 'pickupConfirmedAt',
        direction: 'asc',
    },
};

export const shipmentsOverviewSlice = createSlice({
    name: 'shipmentsOverview',
    initialState,
    reducers: {
        setDateTypeFilter: (state, action: PayloadAction<ShipmentDateType>) => {
            state.filter.dateType = action.payload;
        },
        setStatusFilter: (state, action: PayloadAction<Status[] | undefined>) => {
            state.filter.status = action.payload;
        },
        setDateRangeFilter: (state, action: PayloadAction<DateRangeFilter>) => {
            state.filter.dateRange = action.payload;
        },
        setSearchFilter: (state, action: PayloadAction<string>) => {
            state.filter.search = action.payload;
        },
        setHandlingUnitCodeFilter: (state, action: PayloadAction<string[]>) => {
            state.filter.handlingUnitCodes = action.payload;
        },
        setSorting: (state, action: PayloadAction<ShipmentsOverviewState['sorting']>) => {
            state.sorting = action.payload;
        },
    },
});

export const shipmentsOverviewActions = shipmentsOverviewSlice.actions;

export const getDateTypeFilter = (state: RootState) => state[shipmentsOverviewSlice.reducerPath].filter.dateType;
export const getStatusFilter = (state: RootState) => state[shipmentsOverviewSlice.reducerPath].filter.status;
export const getDateRangeFilter = (state: RootState) => state[shipmentsOverviewSlice.reducerPath].filter.dateRange;
export const getSearchFilter = (state: RootState) => state[shipmentsOverviewSlice.reducerPath].filter.search;
export const getHandlingUnitCodeFilter = (state: RootState) =>
    state[shipmentsOverviewSlice.reducerPath].filter.handlingUnitCodes;
export const getSorting = (state: RootState) => state[shipmentsOverviewSlice.reducerPath].sorting;
export const useDateRangeFilter = () => {
    const filter = useAppSelector(getDateRangeFilter);
    return {
        from: new Date(Date.parse(filter.from)),
        to: new Date(Date.parse(filter.to)),
    };
};
